import React from "react";
import FilterPosts from "../components/FilterPosts/FilterPosts";
import PostCard from "../components/PostCard/PostCard";
import PostCardSkeleton from "../components/PostCard/PostCardSkeleton";
import { BsPencilSquare, BsSearch } from "react-icons/bs";
import { HiAdjustmentsHorizontal, HiXMark } from "react-icons/hi2";
import useViewPostsFacade from "../facades/useViewPostsFacade";

function ViewPosts() {
  const {
    loading,
    filteredPosts,
    searchTerm,
    setSearchTerm,
    showFilters,
    setShowFilters,
    handleFilter,
    resetAllFilters,
    resetToggle,
    navigate,

    postScope,
    setPostScope,
    myCollegeName,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useViewPostsFacade();

  return (
    <div className="bg-[#fcfcfd] min-h-screen pb-20 selection:bg-blue-100">
      {/* --- Section 1: Header --- */}
      <header className="bg-white border-b border-slate-100 pt-12 pb-10 mb-8">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="space-y-1">
              <h1 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight">
                Interview <span className="text-blue-600">Archive.</span>
              </h1>
              <p className="text-slate-500 font-medium text-lg">
                Showing {filteredPosts.length} shared journeys from the community.
              </p>
            </div>

            <button
              onClick={() => navigate(`/create?scope=${postScope}`)}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3.5 px-7 rounded-2xl shadow-lg shadow-blue-200 transition-all active:scale-95 whitespace-nowrap"
            >
              <BsPencilSquare size={20} />
              Share Experience
            </button>
          </div>

          {/*  Toggle buttons (Global / MyCampus) */}
          <div className="mt-8 flex bg-slate-100 p-1.5 rounded-2xl w-full max-w-sm">
            <button
              type="button"
              onClick={() => setPostScope("global")}
              className={`flex-1 py-3 rounded-xl text-xs font-black uppercase transition-all
                ${postScope === "global"
                  ? "bg-white shadow-sm text-blue-600"
                  : "text-slate-500 hover:text-slate-800"
                }`}
            >
              Global
            </button>

            <button
              type="button"
              onClick={() => setPostScope("campus")}
              disabled={!myCollegeName}
              title={!myCollegeName ? "Add your College Name in profile to enable MyCampus" : ""}
              className={`flex-1 py-3 rounded-xl text-xs font-black uppercase transition-all
                ${postScope === "campus"
                  ? "bg-white shadow-sm text-blue-600"
                  : "text-slate-500 hover:text-slate-800"
                }
                ${!myCollegeName ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              MyCampus
            </button>
          </div>
        </div>
      </header>

      {/* --- Section 2: Search & Filter --- */}
      <div className="max-w-6xl mx-auto px-6 mb-10">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-grow group">
              <BsSearch
                className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-600 transition-colors"
                size={18}
              />
              <input
                type="text"
                placeholder="Search company or role..."
                className="w-full pl-14 pr-5 py-4 bg-white border border-slate-200 rounded-3xl focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 transition-all text-slate-800 font-medium shadow-sm outline-none"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <button
              onClick={() => {
                if (showFilters) {
                  handleFilter(null); // Clear dropdown filters on close
                }
                setShowFilters(!showFilters);
              }}
              className={`flex items-center gap-2 py-4 px-6 rounded-3xl font-bold transition-all border ${showFilters
                ? "bg-slate-900 border-slate-900 text-white"
                : "bg-white border-slate-200 text-slate-600 hover:border-blue-400 hover:text-blue-600"
                }`}
            >
              {showFilters ? <HiXMark size={20} /> : <HiAdjustmentsHorizontal size={20} />}
              <span className="hidden sm:inline">{showFilters ? "Close" : "Filters"}</span>
            </button>
          </div>

          {showFilters && (
            <div className="bg-slate-50/50 border border-slate-100 p-4 rounded-3xl animate-in fade-in slide-in-from-top-4 duration-300">
              <div className="flex items-center gap-4">
                <FilterPosts onFilter={handleFilter} resetToggle={resetToggle} />
                <button
                  onClick={resetAllFilters}
                  className="ml-auto text-sm font-bold text-blue-600 hover:underline"
                >
                  Reset All
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* --- Section 3: Feed --- */}
      <main className="max-w-6xl mx-auto px-6">
        {loading ? (
          <div className="grid grid-cols-1 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="w-full">
                <PostCardSkeleton />
              </div>
            ))}
          </div>
        ) : filteredPosts.length > 0 ? (
          <div className="flex flex-col gap-6">
            <div className="grid grid-cols-1 gap-6">
              {filteredPosts.map((post) => (
                <div key={post._id} className="w-full">
                  <PostCard post={post} />
                </div>
              ))}
            </div>
            
            {hasNextPage && (
              <div className="flex justify-center mt-8 mb-4">
                <button
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-3 px-8 rounded-full transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {isFetchingNextPage ? "Loading..." : "Load More Experiences"}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-32 bg-white rounded-[2.5rem] border-2 border-dashed border-slate-100">
            <div className="text-5xl mb-6">🔎</div>
            <h3 className="text-2xl font-bold text-slate-800">No matching experiences</h3>
            <p className="text-slate-500 mt-2 max-w-sm mx-auto text-lg">
              We couldn't find anything for your search. Try a different term or reset your filters.
            </p>
            <button
              onClick={resetAllFilters}
              className="mt-8 px-8 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition-colors"
            >
              Clear All Filters
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

export default ViewPosts;